import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useMap, useMapsLibrary } from '@vis.gl/react-google-maps'

import { useDebounced } from '@/hooks/use-debounced'

export type PlacePrediction = {
    placeId: string
    mainText: string
    secondaryText: string
}

/**
 * `query` = chuỗi (đã trim) mà trạng thái này thuộc về. Trong 300ms debounce,
 * gợi ý đang hiện vẫn là của chuỗi CŨ — so `query` với ô nhập để biết.
 */
export type PredictionsState =
    | { status: 'idle' | 'loading' | 'error'; query: string; predictions: [] }
    | { status: 'ok'; query: string; predictions: PlacePrediction[] }

const IDLE: PredictionsState = { status: 'idle', query: '', predictions: [] }

/**
 * Gợi ý địa điểm của Google (Places API bản cũ — key của dự án chỉ bật bản
 * này) cho chuỗi đang gõ, debounce 300ms, ưu tiên Việt Nam và vùng đang nhìn.
 * `query` rỗng hoặc `enabled=false` (vd chuỗi là toạ độ) thì không gọi Google.
 */
export function usePlacePredictions(
    query: string,
    enabled: boolean,
): PredictionsState & {
    /** Token của phiên gợi ý hiện tại — truyền cho getDetails khi chọn. */
    getSessionToken: () => google.maps.places.AutocompleteSessionToken | null
    /** Kết thúc phiên (sau khi chọn) để lần gõ sau là phiên mới. */
    resetSession: () => void
} {
    const map = useMap()
    const places = useMapsLibrary('places')
    const debounced = useDebounced(query.trim(), 300)
    const [state, setState] = useState<PredictionsState>(IDLE)
    const service = useMemo(
        () => (places ? new places.AutocompleteService() : null),
        [places],
    )
    // Một phiên gợi ý (gõ → chọn) dùng chung một token để Google tính phí
    // theo phiên, không theo từng ký tự. Giữ trong ref: đổi token không được
    // gọi lại gợi ý.
    const tokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(
        null,
    )
    const reqRef = useRef(0)

    const getSessionToken = useCallback(() => {
        if (!tokenRef.current && places) {
            tokenRef.current = new places.AutocompleteSessionToken()
        }
        return tokenRef.current
    }, [places])
    const resetSession = useCallback(() => {
        tokenRef.current = null
    }, [])

    useEffect(() => {
        const req = ++reqRef.current
        if (!service || !enabled || debounced === '') {
            setState(IDLE)
            return
        }
        setState({ status: 'loading', query: debounced, predictions: [] })
        const bounds = map?.getBounds()
        const sessionToken = getSessionToken()
        void service
            .getPlacePredictions(
                {
                    input: debounced,
                    componentRestrictions: { country: 'vn' },
                    ...(bounds ? { locationBias: bounds } : {}),
                    ...(sessionToken ? { sessionToken } : {}),
                },
                (results, status) => {
                    // Bỏ kết quả của lần gõ cũ về muộn hơn lần gõ mới.
                    if (req !== reqRef.current) return
                    if (status === 'OK' || status === 'ZERO_RESULTS') {
                        setState({
                            status: 'ok',
                            query: debounced,
                            predictions: (results ?? []).map((p) => ({
                                placeId: p.place_id,
                                mainText: p.structured_formatting.main_text,
                                secondaryText:
                                    p.structured_formatting.secondary_text ??
                                    '',
                            })),
                        })
                        return
                    }
                    setState({
                        status: 'error',
                        query: debounced,
                        predictions: [],
                    })
                },
            )
            ?.catch(() => {
                // Lỗi đã được báo qua callback; nuốt promise reject để không ra
                // "unhandled rejection".
            })
    }, [service, enabled, debounced, map, getSessionToken])

    return { ...state, getSessionToken, resetSession }
}
